<template>
    <div class="annual-data">
        <div v-if="!isUserInLA">
            Annual data can only be viewed for LA users.
        </div>
        <div v-else>
            <div v-if="retrieveAnnualDataError">
                {{ retrieveErrorMsg }}
            </div>
            <div v-else>
                <b-button
                    type="button"
                    class="float-right"
                    variant="primary"
                    @click="downloadAnnualData"
                >
                    Download Annual Data
                </b-button>
                <p
                    class="annual-data__description"
                    v-for="(item, id) in detailFields"
                    :key="id"
                >
                    <span class="annual-data__description annual-data__description--bold">
                        {{ item.title }}:
                    </span>
                    {{ item.value }}
                </p>
                <b-table
                    class="mt-5 annual-data-table"
                    sticky-header="600px"
                    :striped="true"
                    :bordered="false"
                    responsive="md"
                    :items="annualDataPersonRecords"
                    :fields="annualDataTableFields"
                    :sort-compare="sortCompare"
                    :per-page="rowsPerPage"
                    :current-page="currentPage"
                ></b-table>
                <div class="annual-data__footer">
                    <b-pagination
                        :total-rows="annualDataPersonRecords.length"
                        :per-page="rowsPerPage"
                        prev-text="Prev"
                        next-text="Next"
                        :current-page="currentPage"
                        v-model="currentPage"
                    ></b-pagination>
                    <div v-if="isAllowedToReview">
                        <b-button
                            type="button"
                            variant="primary"
                            size="md"
                            class="float-right"
                            @click="showConfirmModal"
                        >
                            <span class="button-text">Confirm</span>
                        </b-button>
                        <b-button
                            type="button"
                            variant="link"
                            size="md"
                            class="float-right"
                            @click="showCancelModal"
                        >
                            <span class="button-text">Cancel</span>
                        </b-button>
                    </div>
                </div>
                <b-modal
                    ref="cancelModal"
                    centered
                    title="Annual Data Not Confirmed"
                    size="lg"
                    ok-title="No, return to Annual Data"
                    cancel-title="Yes, cancel"
                    header-bg-variant="success"
                    header-text-variant="light"
                    class="tos-modal"
                    hide-header-close
                    no-close-on-backdrop
                    @cancel="handleCancel"
                >
                    <p>You have not confirmed your organization's Annual Data. Are you sure you want to leave?</p>
                </b-modal>
                <b-modal
                    ref="confirmModal"
                    centered
                    title="Confirm Annual Data"
                    size="lg"
                    cancel-title="No, return to Annual Data"
                    ok-title="Yes, confirm"
                    header-bg-variant="success"
                    header-text-variant="light"
                    class="tos-modal"
                    hide-header-close
                    no-close-on-backdrop
                    @ok="handleConfirm"
                >
                    <p>
                        Are you ready to confirm your organization's Annual Data?
                    </p>
                    <p>
                        NOTE: Once you have confirmed your data, the <b>Review Annual Data</b> button will disappear from your dashboard.
                        If you need to update any confirmed data before the January 12 deadline, make the changes in Lighthouse
                        and go to Data > Annual Report to reconfirm your organization's data.
                    </p>
                </b-modal>
                <b-modal
                    ref="confirmedModal"
                    centered
                    title="Annual Data Confirmed"
                    size="lg"
                    ok-only
                    header-bg-variant="success"
                    header-text-variant="light"
                    class="tos-modal"
                    hide-header-close
                    no-close-on-backdrop
                    @ok="handleConfirmed"
                >
                    <p>
                        Thank you for reviewing your organization's annual data.
                        <p v-if="confirmAnnualDataError">
                            There was an error while sending a download of your confirmed data to your email address.
                            Please click Download Annual Data to download your confirmed data.
                        </p>
                        <span v-else>
                            A download of your confirmed data has been sent to your email address.
                        </span>
                    </p>
                </b-modal>
                <b-modal
                    ref="confirmErrorModal"
                    centered
                    title="Error"
                    size="lg"
                    ok-only
                    header-bg-variant="danger"
                    header-text-variant="light"
                    class="tos-modal"
                    hide-header-close
                    no-close-on-backdrop
                    @ok="hideConfirmErrorModal"
                >
                    <p>
                        An error occurred while confirming your data. Please try again.
                    </p>
                </b-modal>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import annualDataMixin from '../../mixins/annualData'
import userPermissionMixin from '@/mixins/userPermission.js'
import userStateMixin from '@/mixins/userState.js'
import jsonToCsvMixin from '@/mixins/jsonToCsv.js'

import { ANNUAL_DATA_TABLE_HEADER, ANNUAL_DATA_DATE_FIELDS } from '@/helpers/enums.js'

export default {
    name: 'ReviewAnnualData',

    mixins: [annualDataMixin, userPermissionMixin, userStateMixin, jsonToCsvMixin],

    data () {
        return {
            currentPage: 1,
            rowsPerPage: 25
        }
    },

    computed: {
        ...mapGetters({
            userCurrentContext: 'userCurrentContext',
            annualDataMetadata: 'getAnnualDataMetadata',
            annualDataPersonRecords: 'getAnnualDataPersonRecords',
            confirmAnnualDataSuccess: 'getConfirmAnnualDataSuccess',
            confirmAnnualDataError: 'getConfirmAnnualDataError',
            retrieveAnnualDataError: 'getRetrieveAnnualDataError',
            isAnnualDataConfirmed: 'getIsAnnualDataConfirmed'
        }),

        authenticatedUser () {
            return this.$auth.user()
        },

        annualDataFileName () {
            const agencyName = this.agencyName ? this.agencyName.split(' ').join('') : ''
            return `${this.year}-AnnualData-${agencyName}.csv`
        },

        annualDataTableFields () {
            return ANNUAL_DATA_TABLE_HEADER.map((h) => {
                return {
                    key: h,
                    label: h + '    ',
                    sortable: true,
                    thStyle: {
                        'white-space': 'pre'
                    }
                }
            })
        },

        agencyName () {
            return _.get(this.userCurrentContext, 'organization.title', '')
        },

        isReviewNeeded () {
            return !this.isAnnualDataConfirmed && this.isWithinAnnualDataReviewWindow
        },

        metadata () {
            const defaultMetadata = {
                'Agency': this.agencyName,
                'Confirmed by': '',
                'Email': '',
                'Confirmation Date': '',
                'Total Records': this.annualDataPersonRecords.length
            }
            return this.isReviewNeeded
                ? defaultMetadata
                : (
                    this.annualDataMetadata['Agency']
                    ? this.annualDataMetadata
                    : defaultMetadata
                )
        },

        detailFields () {
            return Object.keys(this.metadata).map((k) => {
                return {
                    title: k,
                    value: this.metadata[k]
                }
            })
        },

        year () {
            return this.$route.params['year']
        },

        retrieveErrorMsg () {
            return this.retrieveAnnualDataError === 404
                ? `No record exists for year ${this.year}`
                : 'An error occurred while retrieving annual data. Please try reloading the page.'
        },

        isAllowedToReview () {
            const isAllowed = this.isWithinAnnualDataReviewWindow && this.isOrgManagerOrAdmin
            return !this.isAnnualDataConfirmed
                ? isAllowed
                : isAllowed && this.isAnnualDataConfirmed === this.authenticatedUser.id
        }
    },

    watch: {
        year (newValue) {
            if (newValue && this.isUserInLA) {
                this.getAnnualDataPersonRecords()
            }
        }
    },

    beforeRouteLeave (to, from, next) {
        if (this.isReviewNeeded && this.isUserInLA && this.authenticatedUser.id) {
            this.showCancelModal()
            this.$refs.cancelModal.$once('hide', (e) => {
                if (e.trigger === 'cancel') {
                    next()
                }
            })
        } else {
            next()
        }
    },

    created () {
        if (!this.isUserInLA) {
            return
        }

        const user = this.$auth.user()
        if ((!user || !user.id) && (this.isReviewNeeded || this.year === this.reviewYear)) {
            window.location.reload()
        }

        this.getAnnualDataPersonRecords()
    },

    methods: {
        getAnnualDataPersonRecords () {
            this.$store.dispatch('getAnnualDataPersonRecords', {
                year: this.year || '',
                startReviewFromCurrentDate: this.isStartReviewFromCurrentDate
            })
        },

        sortCompare (a, b, key) {
            if (!ANNUAL_DATA_DATE_FIELDS.includes(key)) {
                return false
            }

            if (!a[key]) {
                return -1
            }

            if (!b[key]) {
                return 1
            }

            return this.$moment.utc(b[key], 'MM/DD/YYYY') > this.$moment.utc(a[key], 'MM/DD/YYYY')
                ? -1
                : 1
        },

        showCancelModal () {
            if (this.$refs.cancelModal) {
                this.$refs.cancelModal.show()
            }
        },

        hideCancelModal () {
            if (this.$refs.cancelModal) {
                this.$refs.cancelModal.hide()
            }
        },

        handleCancel () {
            this.hideCancelModal()
            this.$router.push({ path: '/', query: this.$route.query })
        },

        showConfirmModal () {
            if (this.$refs.confirmModal) {
                this.$refs.confirmModal.show()
            }
        },

        hideConfirmModal () {
            if (this.$refs.confirmModal) {
                this.$refs.confirmModal.hide()
            }
        },

        showConfirmErrorModal () {
            if (this.$refs.confirmErrorModal) {
                this.$refs.confirmErrorModal.show()
            }
        },

        hideConfirmErrorModal () {
            if (this.$refs.confirmErrorModal) {
                this.$refs.confirmErrorModal.hide()
            }
        },

        async handleConfirm () {
            await this.$store.dispatch('confirmAnnualData', {
                startReviewFromCurrentDate: this.isStartReviewFromCurrentDate
            })

            if (this.confirmAnnualDataSuccess || this.confirmAnnualDataError === 'annual_data_email_error') {
                this.showConfirmedModal()
            } else if (this.confirmAnnualDataError) {
                this.showConfirmErrorModal()
            }

            this.hideConfirmModal()
        },

        showConfirmedModal () {
            if (this.$refs.confirmedModal) {
                this.$refs.confirmedModal.show()
            }
        },

        hideConfirmedModal () {
            if (this.$refs.confirmedModal) {
                this.$refs.confirmedModal.hide()
            }
        },

        handleConfirmed () {
            this.hideConfirmedModal()

            if (this.confirmAnnualDataSuccess) {
                this.$router.push({ path: '/' })
            }
        },

        downloadAnnualData () {
            const metadataToCsv = Object.keys(this.metadata).reduce((output, key) => {
                output += `${key}, ${this.metadata[key]}\r\n`
                return output
            }, '')
            const dataCsv = this.jsonToCsv(this.annualDataPersonRecords, ANNUAL_DATA_TABLE_HEADER)

            const finalCsv = metadataToCsv + `\r\n` + dataCsv
            this.downloadCsv(finalCsv, this.annualDataFileName)
        }
    }
}
</script>

<style lang="scss" scoped>
.annual-data {
    &__description {
        margin-bottom: 0;

        &--bold {
            font-weight: 500;
        }
    }

    &__footer {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    }
}
</style>


// WEBPACK FOOTER //
// src/views/t1/ReviewAnnualData.vue